<template>
  <div v-if="items.length" :data-testid="$attrs['data-testid'] || null" class="cl-CommentItems">
    <CommentItem
      v-for="(comment, commentIndex) in items"
      :key="comment.id"
      :auto-scroll-to-form-mode
      :can-be-replied="canBeReplied"
      :emoji-picker-append-to
      :hide-actions="hideActions"
      :hide-reply-button="hideReplyButton"
      :is-last="commentIndex === items.length - 1"
      :is-reply-comment="isReplyComment"
      :item="comment"
      :objective="objective"
      :parent-reply-form-open="parentReplyFormOpen"
      :readonly-reactions="readonlyReactions"
      :replies-to-show
      :show-replies-connections
      :table-actions-append-to
      :tracking-source="EVENT_SOURCES.FORM"
      :user-data="userData"
      @remove="emit('remove', $event)"
      @reply="emit('reply', $event)"
      @update="emit('update', $event)"
      @update-reactions="emit('update-reactions', $event)"
      @create-reply="emit('update-from-data', $event)"
      @update-reply="emit('update', $event)"
    />
  </div>

  <InfiniteScrollLoader
    v-if="infiniteLoading"
    :identifier="infiniteId"
    @infinite="emit('infinite-load', $event)"
  >
    <template #loader>
      <slot name="loading">
        <CommentsListLoader />
      </slot>
    </template>
    <template #no-results>
      {{ '' }}
    </template>
  </InfiniteScrollLoader>
</template>

<script setup>
import { ref } from 'vue'

import { EVENT_SOURCES } from '@/tracking/amplitude-helpers'
import { validateRepliesToShowCount } from '@/utils/comments'
import { includesValue } from '@/utils/prop-validators'
import { ELEMENT_VISIBILITY_CHECK_MODE } from '@/utils/window'

import CommentItem from '@/components/objectives/forms/objective-form/CommentItem'
import InfiniteScrollLoader from '@/components/ui/InfiniteScrollLoader/InfiniteScrollLoader'
import CommentsListLoader from '@/components/ui/SkeletonLoaders/CommentsListLoader'

defineOptions({
  name: 'CommentList',
  inheritAttrs: false
})

defineProps({
  items: {
    type: Array,
    required: true
  },

  infiniteLoading: {
    type: Boolean
  },

  userData: {
    type: Object,
    required: true
  },

  objective: {
    type: Object,
    required: true
  },

  canBeReplied: {
    type: Boolean
  },

  hideActions: {
    type: Boolean
  },

  hideReplyButton: {
    type: Boolean
  },

  isReplyComment: {
    type: Boolean
  },

  parentReplyFormOpen: {
    type: Boolean
  },

  repliesToShow: {
    type: Number,
    default: Infinity,
    validator: v => validateRepliesToShowCount(v)
  },

  showRepliesConnections: {
    type: Boolean,
    default: true
  },

  autoScrollToFormMode: {
    type: String,
    default: ELEMENT_VISIBILITY_CHECK_MODE.SCROLLABLE_PARENT,
    validator: v => includesValue(v, ELEMENT_VISIBILITY_CHECK_MODE)
  },

  tableActionsAppendTo: {
    type: String,
    default: ''
  },

  emojiPickerAppendTo: {
    type: String,
    default: ''
  },

  readonlyReactions: {
    type: Boolean
  }
})

const emit = defineEmits({
  reply: null,
  remove: null,
  update: null,
  'update-from-data': null,
  'infinite-load': null,
  'update-reactions': null
})
const infiniteId = ref(+new Date())

const updateItems = () => {
  infiniteId.value += 1
}

defineExpose({
  updateItems
})
</script>

<style lang="scss" scoped>
.cl-CommentItems {
  display: flex;
  flex-direction: column;
  gap: 24px;
}
</style>
